/**
 * HTTP Client for ShareWifi Management API
 * 
 * This module provides a centralized HTTP client for making API calls
 * to the backend server with authentication and error handling.
 */

// Import toast for user notifications
import { toast } from 'sonner';

// API Configuration
const API_CONFIG = {
  // Use environment variable or default to localhost
  baseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1',
  timeout: 30000,
};

// Rate limit tracking
let rateLimitToastShown = false;
let rateLimitResetTimeout: NodeJS.Timeout | null = null;

/**
 * Get the stored auth token
 */
function getAuthToken(): string | null {
  const authData = localStorage.getItem('sharewifi_auth');
  if (authData) {
    try {
      const parsed = JSON.parse(authData);
      return parsed.token || null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Set the auth token
 */
export function setAuthToken(token: string, user: any): void {
  localStorage.setItem('sharewifi_auth', JSON.stringify({ token, user }));
}

/**
 * Clear auth token
 */
export function clearAuthToken(): void {
  localStorage.removeItem('sharewifi_auth');
}

/**
 * Get stored user data
 */
export function getStoredUser(): any | null {
  const authData = localStorage.getItem('sharewifi_auth');
  if (authData) {
    try {
      const parsed = JSON.parse(authData);
      return parsed.user || null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * API Error class for structured error handling
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * HTTP request options
 */
interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: any;
  headers?: Record<string, string>;
  skipAuth?: boolean;
}

/**
 * Make an HTTP request to the API
 */
async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, skipAuth = false } = options;
  
  const url = `${API_CONFIG.baseUrl}${endpoint}`;
  
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };
  
  // Add auth token if available and not skipped
  if (!skipAuth) {
    const token = getAuthToken();
    if (token) {
      requestHeaders['Authorization'] = `Bearer ${token}`;
    }
  }
  
  const fetchOptions: RequestInit = {
    method,
    headers: requestHeaders,
  };
  
  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }
  
  try {
    const response = await fetch(url, fetchOptions);
    
    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }
    
    // Try to parse JSON response
    let data: any;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }
    
    // Handle error responses
    if (!response.ok) {
      const errorMessage = data?.error?.message || data?.message || `Request failed with status ${response.status}`;
      const errorCode = data?.error?.code || 'UNKNOWN_ERROR';
      
      // Handle 401 Unauthorized - clear auth and redirect to login
      if (response.status === 401) {
        clearAuthToken();
        // Only redirect if not already on login page to avoid redirect loop
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
      
      // Handle 429 Rate Limit - show user-friendly message once
      if (response.status === 429) {
        // Only show toast once to avoid spam
        if (!rateLimitToastShown) {
          rateLimitToastShown = true;
          
          toast.warning('Please slow down', {
            description: 'You\'re making requests too quickly. Please wait a moment and try again.',
            duration: 5000,
          });
          
          // Reset the flag after 5 seconds
          if (rateLimitResetTimeout) {
            clearTimeout(rateLimitResetTimeout);
          }
          rateLimitResetTimeout = setTimeout(() => {
            rateLimitToastShown = false;
            rateLimitResetTimeout = null;
          }, 5000);
        }
        
        // Throw error but with a more user-friendly message
        throw new ApiError(429, 'RATE_LIMIT_EXCEEDED', 'Rate limit exceeded. Please wait a moment.', data);
      }
      
      throw new ApiError(response.status, errorCode, errorMessage, data);
    }
    
    return data as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    
    // Network error or other fetch error
    throw new ApiError(0, 'NETWORK_ERROR', 'Network error: Unable to connect to the server');
  }
}

/**
 * HTTP Client with convenience methods
 */
export const httpClient = {
  get: <T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...options, method: 'GET' }),
    
  post: <T>(endpoint: string, body?: any, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...options, method: 'POST', body }),
    
  put: <T>(endpoint: string, body?: any, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...options, method: 'PUT', body }),
    
  patch: <T>(endpoint: string, body?: any, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...options, method: 'PATCH', body }),
    
  delete: <T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),
};

/**
 * Build query string from params object
 */
export function buildQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Check if an error is a rate limit error
 */
export function isRateLimitError(error: any): boolean {
  return error instanceof ApiError && error.status === 429;
}

/**
 * Handle API errors gracefully
 * Returns true if the error was handled (e.g., rate limit), false if it should be shown to user
 */
export function handleApiError(error: any, context?: string): boolean {
  if (isRateLimitError(error)) {
    // Rate limit errors are already shown via toast in HttpClient
    // Just log to console for debugging
    console.warn(`Rate limit hit${context ? ` (${context})` : ''}`);
    return true; // Error handled, don't show additional UI error
  }
  return false; // Error not handled, show to user
}

export default httpClient;
